import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap, throwError } from "rxjs";
import { BaseRespone, PaginationResult } from "src/app/models/common.response";
import { environment } from "src/environments/environment";
import { LoginService } from "src/app/login.service";
import { ITemplate, ITemplateRequest } from "./template.interface";

@Injectable({ providedIn: 'root' })
export class TemplateService {

    private _template: BehaviorSubject<ITemplate | null> = new BehaviorSubject<ITemplate | null>(null);
    private _templates: BehaviorSubject<ITemplate[]> = new BehaviorSubject<ITemplate[]>([]);
    private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject<PaginationResult | null>(null);

    constructor(
        private _httpClient: HttpClient,
        private _loginService: LoginService
    ) { }

    get template$(): Observable<ITemplate | null> {
        return this._template.asObservable();
    }

    get templates$(): Observable<ITemplate[]> {
        return this._templates.asObservable();
    }

    get pagination$(): Observable<PaginationResult | null> {
        return this._pagination.asObservable();
    }

    getAllTemplates(template: ITemplateRequest): Observable<BaseRespone<{ pagination: PaginationResult; templateDetails: ITemplate[] }>> {
        const requestPayload: ITemplateRequest = {
            userId: this._loginService.getUserId(),
            pageNumber: template.pageNumber ?? template.currentPage ?? 0,
            pageSize: template.pageSize ?? 100,
            templateType: template.templateType,
            searchValue: template.searchValue,
            searchProperty: template.searchProperty,
            searchComapreOption: template.searchComapreOption
        };
        return this._httpClient.post<BaseRespone<{ pagination: PaginationResult; templateDetails: ITemplate[] }>>(`${environment.baseURl}/Template/list`, requestPayload).pipe(
            tap((response) => {
                this._pagination.next(response.result.pagination);
                this._templates.next(response.result.templateDetails);
            }),
            catchError((error) => {
                console.error('Error fetching templates:', error);
                throw error;
            })
        );
    }

    createTemplate(templateDetails: ITemplate): Observable<BaseRespone<ITemplate>> {
        return this._httpClient.post<BaseRespone<ITemplate>>(`${environment.baseURl}/Template`, templateDetails)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newTemplate) => {
                    return newTemplate;
                })
            )
    }

    updateTemplate(templateDetails: ITemplate): Observable<BaseRespone<ITemplate>> {
        return this.templates$.pipe(
            take(1),
            switchMap(templates => this._httpClient.put<BaseRespone<ITemplate>>(`${environment.baseURl}/Template`, templateDetails).pipe(
                map((updated: BaseRespone<ITemplate>) => {
                    const index = templates.findIndex(item => item.id === templateDetails.id);
                    if (index !== -1) {
                        templates[index] = templateDetails;
                    }
                    this._templates.next(templates);
                    return updated;
                })
            )), catchError((error) => {
                console.error('Error updating template:', error);
                throw error;
            })
        );
    }

    deleteTemplate(id: number): Observable<BaseRespone<boolean>> {
        return this.templates$.pipe(
            take(1),
            switchMap(templates =>
                this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseURl}/Template/${id}`).pipe(
                    map((deletedResponse) => {
                        if (deletedResponse.result) {
                            const updated = templates.filter(item => item.id !== id);
                            this._templates.next(updated);
                        }
                        return deletedResponse;
                    })
                )
            ), catchError((error) => {
                console.error('Error deleting template:', error);
                throw error;
            })
        );
    }

    getTemplateById(id: number): Observable<BaseRespone<ITemplate>> {
        return this._httpClient.get<BaseRespone<ITemplate>>(`${environment.baseURl}/Template/${id}`).pipe(
            tap((response) => {
                this._template.next(response.result);
            }),
            catchError((error) => {
                return throwError(() => error.error);
            })
        );
    }
}
